import {CopyButton, IconButton, SearchField} from '@wandb/components';
import {Alert} from '@wandb/weave/components/Alert';
import {WaveLoader} from '@wandb/weave/components/Loaders/WaveLoader';
import {Tailwind} from '@wandb/weave/components/Tailwind';
import {useLocalStorage} from '@wandb/weave/util/useLocalStorage';
import React, {useEffect, useRef, useState} from 'react';
import {type ListRange, type VirtuosoHandle} from 'react-virtuoso';
import {twMerge} from 'tailwind-merge';

import {makeDownloadUrl} from '../../util/url';
import {
  COMPACT_LINE_HEIGHT,
  DEFAULT_LINE_HEIGHT,
  JUMP_TO_BOTTOM_THRESHOLD_PX,
  OUTER_PADDING,
  TRUNCATED_LABEL_MAX_CHARS,
} from './constants';
import {DenseLogList} from './DenseLogList';
import {
  clampLogIndex,
  GlobalLogList,
  type GlobalLogViewerPagination,
} from './GlobalLogList';
import {useLogLabels} from './hooks/useLogLabels';
import {useLogSearch} from './hooks/useLogSearch';
import {useLogsFormattedForCopy} from './hooks/useLogsFormattedForCopy';
import {useLogTimestampFormatters} from './hooks/useLogTimestampFormatters';
import {type LogViewerState} from './hooks/useVirtuosoState';
import {useVisibleLogs} from './hooks/useVisibleLogs';
import {type LogLabelFilterOption} from './LogLabelFilter';
import {LogLabelFilter} from './LogLabelFilter';
import {type LogLineData} from './LogLineRenderer';
import {LogSearchBrowser} from './LogSearchBrowser';
import {LogViewerError, type OnRetry} from './LogViewerError';
import {type OnRetryFetchMore} from './LogViewerFetchMoreError';
import {type RenderFetchMoreError} from './LogViewerPageLoader';
import {TimestampVisibilitySelect} from './TimestampVisibilitySelect';

export {type GlobalLogViewerPagination} from './GlobalLogList';
export {type LogViewerState} from './hooks/useVirtuosoState';

export interface LogViewerProps {
  error?: Error | string;
  globalPagination?: GlobalLogViewerPagination;
  isImprovedPaginationEnabled?: boolean;
  loading: boolean;
  lines: LogLineData[];
  outputLogFile: string | null;
  totalLines?: number;
  onTimestampVisibilityChange?: (isVisible: boolean) => void;
  onLabelFiltersApplied?: (
    selectedCount: number,
    isSearchActive: boolean
  ) => void;
  onCopyLogs?: () => void;
  onJumpToBottom?: () => void;
  onLogFileDownload?: () => void;
  onLogSearchResultsNavigated?: () => void;
  onLogsSearched?: (searchText: string) => void;
  onRetry?: OnRetry;
  renderEmpty?: () => React.ReactNode;
  renderError?: (error: Error | string, onRetry?: OnRetry) => React.ReactNode;
  renderLabelsBanner?: () => React.ReactNode;
  renderLimitReachedBanner?: (numLinesAvailable: number) => React.ReactNode;
  renderLoading?: () => React.ReactNode;
  // State persistence
  restoreState?: LogViewerState;
  onStateSaved?: (state: LogViewerState) => void;
  // Backward pagination (older logs)
  hasPrevious?: boolean;
  isLoadingPrevious?: boolean;
  isRetryingPrevious?: boolean;
  loadPreviousError?: Error | string;
  onLoadPrevious?: () => void;
  onRetryPrevious?: OnRetryFetchMore;
  renderFetchPreviousError?: RenderFetchMoreError;
  // Forward pagination (newer logs)
  hasNext?: boolean;
  isLoadingNext?: boolean;
  isRetryingNext?: boolean;
  loadNextError?: Error | string;
  onLoadNext?: () => void;
  onRetryNext?: OnRetryFetchMore;
  renderFetchNextError?: RenderFetchMoreError;
  // Polling control
  isPolling?: boolean;
}

type NumberedLogLine = LogLineData & {lineNum?: number};

export const edgesToNumberedLines = (
  edges: Array<{node: LogLineData}>,
  totalLines?: number
): NumberedLogLine[] => {
  if (!totalLines) {
    return edges.map(({node}) => node);
  }

  return edges.map(({node}, idx) => {
    return {
      ...node,
      lineNum: idx + 1 + Math.max(totalLines - edges.length, 0),
    };
  });
};

/**
 * Core log viewer shared between the app and weave frontends. Owns the chrome
 * (timestamp toggle, label filter, search, copy/download) and the search/filter
 * state that selects the active list, then renders one of two list bodies:
 *
 * - `GlobalLogList` — sparse, line-number-indexed list (global pagination), when
 *   global pagination is enabled and no search/filter is active.
 * - `DenseLogList` — contiguous array-backed list (legacy path, and the
 *   search/filter fallback under global pagination).
 */
export const LogViewer: React.FC<LogViewerProps> = ({
  error,
  globalPagination,
  hasPrevious,
  isImprovedPaginationEnabled = false,
  isLoadingNext,
  isLoadingPrevious,
  isPolling,
  isRetryingNext,
  isRetryingPrevious,
  lines,
  loading,
  loadNextError,
  loadPreviousError,
  onCopyLogs,
  onJumpToBottom,
  onLabelFiltersApplied,
  onLoadPrevious,
  onLogFileDownload,
  onLogSearchResultsNavigated,
  onLogsSearched,
  onRetry,
  onRetryNext,
  onRetryPrevious,
  onStateSaved,
  onTimestampVisibilityChange,
  outputLogFile,
  renderEmpty,
  renderError,
  renderFetchNextError,
  renderFetchPreviousError,
  renderLabelsBanner,
  renderLimitReachedBanner,
  renderLoading,
  restoreState,
  totalLines,
}) => {
  const [isTimestampVisible, setIsTimestampVisible] = useLocalStorage(
    'logTimestampsVisible',
    true
  );

  const [selectedLabels, setSelectedLabels] = useState<
    readonly LogLabelFilterOption[]
  >([]);
  // Keep track of selections when the label filter dropdown opens
  const [selectedLabelsOnMenuOpen, setSelectedLabelsOnMenuOpen] = useState<
    readonly LogLabelFilterOption[] | null
  >(null);

  const {all: allLines, filtered: filteredLines} = useVisibleLogs(
    lines,
    selectedLabels,
    loading
  );

  const allLabels = useLogLabels(allLines);
  const filteredLabels = selectedLabels.length ? selectedLabels : allLabels;

  const timestampFormatters = useLogTimestampFormatters();

  const logsFormattedForCopy = useLogsFormattedForCopy(
    filteredLines,
    filteredLabels,
    isTimestampVisible,
    timestampFormatters
  );

  const lineHeight =
    isTimestampVisible || filteredLabels.length > 0
      ? DEFAULT_LINE_HEIGHT
      : COMPACT_LINE_HEIGHT;

  const virtuosoRef = useRef<VirtuosoHandle>(null);
  const scrollTo = (
    row: number,
    align: 'start' | 'center' | 'end' = 'center'
  ) => {
    virtuosoRef.current?.scrollToIndex({
      index: row,
      align,
      behavior: 'auto',
    });
  };

  const {
    searchText,
    searchActive,
    searchMatches,
    onSearchChange,
    onKeyDown,
    goToNext,
    goToPrev,
  } = useLogSearch(filteredLines, scrollTo, {
    onResultsNavigated: onLogSearchResultsNavigated,
    onSearch: onLogsSearched,
  });

  // Global pagination only drives the indexed list when there's no active
  // search or label filter; otherwise we fall back to the dense list over the
  // currently loaded lines.
  const isGlobalListActive =
    globalPagination != null &&
    searchText === '' &&
    selectedLabels.length === 0;

  const maxLabelLength = Math.min(
    Math.max(...filteredLabels.map(({displayText}) => displayText.length), 0),
    TRUNCATED_LABEL_MAX_CHARS + 1 // +1 for ellipsis character
  );

  // Owned here (not in GlobalLogList) so it persists across the dense interlude
  // and can be restored when the global list re-activates.
  const [globalVisibleRange, setGlobalVisibleRange] =
    useState<ListRange | null>(null);
  const wasGlobalListActiveRef = useRef(isGlobalListActive);
  useEffect(() => {
    const wasGlobalListActive = wasGlobalListActiveRef.current;
    wasGlobalListActiveRef.current = isGlobalListActive;

    // Re-anchor and kick a range fetch when the global list becomes active
    // again after a search/filter interlude — but not on the initial mount,
    // where the first fetch comes from Virtuoso's own range change.
    if (
      !isGlobalListActive ||
      wasGlobalListActive ||
      globalPagination == null ||
      globalPagination.totalCount <= 0
    ) {
      return;
    }

    const total = globalPagination.totalCount;
    const anchorIndex = clampLogIndex(
      globalVisibleRange?.startIndex ??
        globalPagination.initialTopMostItemIndex ??
        total - 1,
      total
    );
    const endIndex = clampLogIndex(
      Math.max(anchorIndex, globalVisibleRange?.endIndex ?? anchorIndex),
      total
    );
    const range = {startIndex: anchorIndex, endIndex};
    setGlobalVisibleRange(range);
    globalPagination.onRangeChanged(range);
    globalPagination.onAtCurrentChange?.(
      total - 1 - range.endIndex <=
        Math.ceil(JUMP_TO_BOTTOM_THRESHOLD_PX / lineHeight)
    );
  }, [globalPagination, globalVisibleRange, isGlobalListActive, lineHeight]);

  if (loading) {
    if (renderLoading) {
      return <>{renderLoading()}</>;
    }

    return <Tailwind>Loading logs…</Tailwind>;
  }

  if (error) {
    if (renderError) {
      return <>{renderError(error, onRetry)}</>;
    }

    return <LogViewerError error={error} onRetry={onRetry} />;
  }

  if (allLines.length === 0 && !globalPagination?.totalCount) {
    if (renderEmpty) {
      return <>{renderEmpty()}</>;
    }

    return (
      <div style={{margin: 10}}>
        <Alert severity="info">No log output available.</Alert>
      </div>
    );
  }

  const isStorageLimitReached =
    !isGlobalListActive &&
    !onLoadPrevious &&
    totalLines &&
    totalLines > allLines.length;

  return (
    <Tailwind style={{height: '100%', overflowY: 'auto'}}>
      <div className={`bg-wb-surface p-${OUTER_PADDING} flex flex-col h-full`}>
        {isStorageLimitReached && renderLimitReachedBanner?.(allLines.length)}
        {allLabels.length > 0 && renderLabelsBanner?.()}
        <div className="flex flex-wrap gap-16 md:flex-nowrap">
          <div className="mt-4 flex w-full flex-wrap gap-8 md:flex-nowrap">
            <div className="w-full min-w-[210px] md:w-auto">
              <TimestampVisibilitySelect
                isTimestampVisible={isTimestampVisible}
                onChange={value => {
                  setIsTimestampVisible(value);
                  onTimestampVisibilityChange?.(value);
                }}
              />
            </div>
            {allLabels.length > 0 && (
              <div className="w-full min-w-[240px] md:w-[35%] md:max-w-[650px]">
                <LogLabelFilter
                  allLabels={allLabels}
                  selectedLabels={selectedLabels}
                  onChange={setSelectedLabels}
                  onMenuOpen={() => {
                    setSelectedLabelsOnMenuOpen(selectedLabels);
                  }}
                  onMenuClose={() => {
                    if (selectedLabels !== selectedLabelsOnMenuOpen) {
                      onLabelFiltersApplied?.(
                        selectedLabels.length,
                        !!searchText
                      );
                    }
                    setSelectedLabelsOnMenuOpen(null);
                  }}
                />
              </div>
            )}
            <div className="flex w-full grow gap-8">
              <div className="grow">
                <SearchField
                  fullWidth
                  aria-label="Search logs"
                  placeholder="Search"
                  value={searchText}
                  onChange={onSearchChange}
                  onKeyDown={e => onKeyDown(e.key)}
                />
              </div>
              {searchText && (
                <LogSearchBrowser
                  index={searchActive}
                  count={searchMatches.ordered.length}
                  goToNext={goToNext}
                  goToPrev={goToPrev}
                />
              )}
            </div>
          </div>
          <div className="flex w-full items-center gap-8 md:w-auto">
            {isPolling && <WaveLoader size="small" />}
            <CopyButton
              content={logsFormattedForCopy}
              size="md"
              onCopy={onCopyLogs}
            />
            {outputLogFile && (
              <IconButton
                icon="download"
                variant="secondary"
                size="md"
                tooltip={{children: 'Download log file'}}
                aria-label="Download log file"
                href={makeDownloadUrl(outputLogFile, 'output.log')}
                download="output.log"
                onClick={onLogFileDownload}
              />
            )}
          </div>
        </div>
        <div
          data-testid="log-viewer"
          style={{
            position: 'relative',
            lineHeight: `${lineHeight}px`,
          }}
          className={twMerge(
            'mt-16 bg-wb-surface-sunken px-16',
            'font-mono text-[13px] flex-1'
          )}>
          {isGlobalListActive && globalPagination != null ? (
            <GlobalLogList
              filteredLabels={filteredLabels}
              filteredLines={filteredLines}
              formatters={timestampFormatters}
              globalPagination={globalPagination}
              isPolling={isPolling}
              isTimestampVisible={isTimestampVisible}
              lineHeight={lineHeight}
              maxLabelLength={maxLabelLength}
              onJumpToBottom={onJumpToBottom}
              onVisibleRangeChange={setGlobalVisibleRange}
              scrollTo={scrollTo}
              virtuosoRef={virtuosoRef}
              visibleRange={globalVisibleRange}
            />
          ) : (
            <DenseLogList
              filteredLabels={filteredLabels}
              filteredLines={filteredLines}
              formatters={timestampFormatters}
              isPolling={isPolling}
              isTimestampVisible={isTimestampVisible}
              lineHeight={lineHeight}
              maxLabelLength={maxLabelLength}
              onJumpToBottom={onJumpToBottom}
              scrollTo={scrollTo}
              virtuosoRef={virtuosoRef}
              searchActive={searchActive}
              searchMatches={searchMatches}
              searchText={searchText}
              isImprovedPaginationEnabled={isImprovedPaginationEnabled}
              restoreState={restoreState}
              onStateSaved={onStateSaved}
              hasPrevious={hasPrevious}
              isLoadingPrevious={isLoadingPrevious}
              isRetryingPrevious={isRetryingPrevious}
              loadPreviousError={loadPreviousError}
              onLoadPrevious={onLoadPrevious}
              onRetryPrevious={onRetryPrevious}
              renderFetchPreviousError={renderFetchPreviousError}
              isLoadingNext={isLoadingNext}
              isRetryingNext={isRetryingNext}
              loadNextError={loadNextError}
              onRetryNext={onRetryNext}
              renderFetchNextError={renderFetchNextError}
            />
          )}
        </div>
      </div>
    </Tailwind>
  );
};
